import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { TextField, IconButton, Box, Typography, FormHelperText, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TagIcon from '@mui/icons-material/Tag';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import {
  CustomTrainingField,
  FieldType,
  CreateFieldRequest,
  EnumOptions,
  RatingOptions,
  FullTeamNumberOptions,
  TargetRecordingOptions,
} from '../../models/training.models';
import { palette } from '../../theme';

export interface CustomTrainingFieldDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (data: CreateFieldRequest) => void;
  isLoading: boolean;
  existingField?: CustomTrainingField | null;
}

export interface FieldTypeConfig {
  label: string;
  icon: React.ReactNode;
  badgeBg: string;
  badgeColor: string;
}

export const FIELD_TYPE_CONFIG: Record<FieldType, FieldTypeConfig> = {
  text: {
    label: 'Text',
    icon: <TextFieldsIcon sx={{ fontSize: 24 }} />,
    badgeBg: '#E8F5E9',
    badgeColor: '#2E7D32',
  },
  number: {
    label: 'Number',
    icon: <TagIcon sx={{ fontSize: 24 }} />,
    badgeBg: '#F5F5F5',
    badgeColor: '#616161',
  },
  rating: {
    label: 'Rating',
    icon: <StarBorderIcon sx={{ fontSize: 24 }} />,
    badgeBg: '#FFF8E1',
    badgeColor: '#F2994A',
  },
  enum: {
    label: 'Dropdown',
    icon: <ExpandMoreIcon sx={{ fontSize: 24 }} />,
    badgeBg: '#EDE7F6',
    badgeColor: '#7E57C2',
  },
  full_team_number: {
    label: 'Per-Athlete',
    icon: <PersonAddAltIcon sx={{ fontSize: 24 }} />,
    badgeBg: '#FCE4EC',
    badgeColor: '#E91E63',
  },
  target_recording: {
    label: 'Biathlon Target',
    icon: <PlayCircleOutlineIcon sx={{ fontSize: 24 }} />,
    badgeBg: '#E3F2FD',
    badgeColor: '#1976D2',
  },
};

const FIELD_TYPE_ORDER: FieldType[] = ['text', 'number', 'rating', 'enum', 'full_team_number', 'target_recording'];

export default function CustomTrainingFieldDialog(props: CustomTrainingFieldDialogProps) {
  const isEditMode = !!props.existingField;

  const [fieldForm, setFieldForm] = useState<CreateFieldRequest>({
    name: '',
    field_type: 'text',
    options: null,
  });

  const [enumOptions, setEnumOptions] = useState<string[]>(['']);
  const [ratingMin, setRatingMin] = useState<number>(1);
  const [ratingMax, setRatingMax] = useState<number>(5);
  const [teamNumberMin, setTeamNumberMin] = useState<number | ''>('');
  const [teamNumberMax, setTeamNumberMax] = useState<number | ''>('');
  const [targetNumShots, setTargetNumShots] = useState<number>(10);

  const [nameError, setNameError] = useState<string>('');
  const [optionsError, setOptionsError] = useState<string>('');

  useEffect(() => {
    if (props.existingField) {
      setFieldForm({
        name: props.existingField.name,
        field_type: props.existingField.field_type,
        options: props.existingField.options,
      });

      if (props.existingField.field_type === 'enum' && props.existingField.options) {
        const opts = props.existingField.options as EnumOptions;
        setEnumOptions(opts.options || ['']);
      } else if (props.existingField.field_type === 'rating' && props.existingField.options) {
        const opts = props.existingField.options as RatingOptions;
        setRatingMin(opts.min || 1);
        setRatingMax(opts.max || 5);
      } else if (props.existingField.field_type === 'full_team_number' && props.existingField.options) {
        const opts = props.existingField.options as FullTeamNumberOptions;
        setTeamNumberMin(opts.min ?? '');
        setTeamNumberMax(opts.max ?? '');
      } else if (props.existingField.field_type === 'target_recording' && props.existingField.options) {
        const opts = props.existingField.options as TargetRecordingOptions;
        setTargetNumShots(opts.num_shots || 10);
      }
    } else {
      setFieldForm({ name: '', field_type: 'text', options: null });
      setEnumOptions(['']);
      setRatingMin(1);
      setRatingMax(5);
      setTeamNumberMin('');
      setTeamNumberMax('');
      setTargetNumShots(10);
    }
    setNameError('');
    setOptionsError('');
  }, [props.existingField, props.isOpen]);

  const closeModal = () => {
    props.handleClose();
  };

  const handleFieldTypeChange = (newType: FieldType) => {
    setFieldForm({ ...fieldForm, field_type: newType, options: null });
    setOptionsError('');
    if (newType === 'enum') {
      setEnumOptions(['']);
    } else if (newType === 'rating') {
      setRatingMin(1);
      setRatingMax(5);
    } else if (newType === 'full_team_number') {
      setTeamNumberMin('');
      setTeamNumberMax('');
    } else if (newType === 'target_recording') {
      setTargetNumShots(10);
    }
  };

  const addEnumOption = () => {
    setEnumOptions([...enumOptions, '']);
  };

  const removeEnumOption = (index: number) => {
    const newOptions = enumOptions.filter((_, i) => i !== index);
    setEnumOptions(newOptions.length > 0 ? newOptions : ['']);
  };

  const updateEnumOption = (index: number, value: string) => {
    const newOptions = [...enumOptions];
    newOptions[index] = value;
    setEnumOptions(newOptions);
  };

  const validateAndSubmit = () => {
    setNameError('');
    setOptionsError('');

    if (!fieldForm.name.trim()) {
      setNameError('Field name is required');
      return;
    }

    if (fieldForm.name.length > 250) {
      setNameError('Field name cannot exceed 250 characters');
      return;
    }

    let options: any = null;

    if (fieldForm.field_type === 'enum') {
      const filteredOptions = enumOptions.filter((opt) => opt.trim() !== '');
      if (filteredOptions.length === 0) {
        setOptionsError('At least one option is required for dropdown fields');
        return;
      }
      options = { options: filteredOptions };
    } else if (fieldForm.field_type === 'rating') {
      if (ratingMin >= ratingMax) {
        setOptionsError('Minimum must be less than maximum');
        return;
      }
      options = { min: ratingMin, max: ratingMax };
    } else if (fieldForm.field_type === 'full_team_number') {
      if (teamNumberMin !== '' || teamNumberMax !== '') {
        if (teamNumberMin !== '' && teamNumberMax !== '' && teamNumberMin >= teamNumberMax) {
          setOptionsError('Minimum must be less than maximum');
          return;
        }
        options = {
          ...(teamNumberMin !== '' && { min: teamNumberMin }),
          ...(teamNumberMax !== '' && { max: teamNumberMax }),
        };
      }
    } else if (fieldForm.field_type === 'target_recording') {
      if (targetNumShots < 1 || targetNumShots > 50) {
        setOptionsError('Number of shots must be between 1 and 50');
        return;
      }
      options = { num_shots: targetNumShots };
    }

    const submitData: CreateFieldRequest = {
      name: fieldForm.name.trim(),
      field_type: fieldForm.field_type,
      options: options,
    };

    props.handleSubmit(submitData);
  };

  const renderLabelInput = (label: string, input: React.ReactNode) => (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
        {label}
      </Typography>
      {input}
    </Box>
  );

  const renderOptionsConfig = () => {
    const panelSx = {
      borderRadius: 2,
      p: 2,
      mt: 2,
    };

    switch (fieldForm.field_type) {
      case 'text':
        return (
          <Paper variant="outlined" sx={panelSx}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
              Text Options
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Free-form text input. Coaches can type any response for this field during training sessions.
            </Typography>
          </Paper>
        );

      case 'number':
        return (
          <Paper variant="outlined" sx={panelSx}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
              Number Options
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Coaches enter a numeric value for this field during training sessions.
            </Typography>
          </Paper>
        );

      case 'rating':
        return (
          <Paper variant="outlined" sx={panelSx}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
              Rating Scale Options
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
              {renderLabelInput(
                'Minimum',
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={ratingMin}
                  onChange={(e) => setRatingMin(Number(e.target.value))}
                />
              )}
              {renderLabelInput(
                'Maximum',
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={ratingMax}
                  onChange={(e) => setRatingMax(Number(e.target.value))}
                />
              )}
            </Box>
          </Paper>
        );

      case 'enum':
        return (
          <Paper variant="outlined" sx={panelSx}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
              Dropdown Options
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {enumOptions.map((option, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    value={option}
                    onChange={(e) => updateEnumOption(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                  />
                  <IconButton
                    onClick={() => removeEnumOption(index)}
                    disabled={enumOptions.length === 1}
                    size="small"
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      bgcolor: palette.error.light,
                      color: palette.error.main,
                      borderRadius: '50%',
                      '&:hover': { bgcolor: palette.error.main, color: '#fff' },
                      '&.Mui-disabled': { bgcolor: palette.grey[100], color: palette.grey[400] },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={addEnumOption}
                size="small"
                sx={{
                  color: palette.primary.main,
                  '&:hover': { bgcolor: palette.grey[100] },
                }}
              >
                Add Option
              </Button>
            </Box>
          </Paper>
        );

      case 'full_team_number':
        return (
          <Paper variant="outlined" sx={panelSx}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
              Number Range Options
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 1.5 }}>
              Optional — leave blank to allow any numeric input.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {renderLabelInput(
                'Minimum',
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={teamNumberMin}
                  onChange={(e) => setTeamNumberMin(e.target.value ? Number(e.target.value) : '')}
                  placeholder="None"
                />
              )}
              {renderLabelInput(
                'Maximum',
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={teamNumberMax}
                  onChange={(e) => setTeamNumberMax(e.target.value ? Number(e.target.value) : '')}
                  placeholder="None"
                />
              )}
            </Box>
          </Paper>
        );

      case 'target_recording':
        return (
          <Paper variant="outlined" sx={panelSx}>
            <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
              Biathlon Target Options
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {renderLabelInput(
                'Number of Shots',
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  value={targetNumShots}
                  onChange={(e) => setTargetNumShots(Number(e.target.value))}
                  helperText="Number of shots each athlete can record (1–50)"
                  inputProps={{ min: 1, max: 50 }}
                />
              )}
            </Box>
          </Paper>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={closeModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1, width: 560, maxWidth: '100%' } }}
    >
      {/* Title row */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: 'text.primary' }}>
          {isEditMode ? 'Edit Custom Field' : 'New Custom Field'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={closeModal}
          size="small"
          sx={{
            color: palette.text.secondary,
            '&:hover': { bgcolor: palette.grey[100] },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ height: 520, overflow: 'auto' }}>
        {/* Field Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
            Field Name
          </Typography>
          <TextField
            required
            name="fieldName"
            value={fieldForm.name}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="e.g. Coach Notes"
            onChange={(evt) => setFieldForm({ ...fieldForm, name: evt.target.value })}
            error={!!nameError}
            helperText={nameError || `${fieldForm.name.length}/250 characters`}
            inputProps={{ maxLength: 250 }}
          />
        </Box>

        {/* Field Type card grid */}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
            Field Type
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
            }}
          >
            {FIELD_TYPE_ORDER.map((type) => {
              const config = FIELD_TYPE_CONFIG[type];
              const isSelected = fieldForm.field_type === type;
              return (
                <Box
                  key={type}
                  onClick={() => handleFieldTypeChange(type)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    py: 2,
                    px: 1,
                    borderRadius: 2,
                    border: isSelected ? `2px solid ${palette.primary.main}` : `1px solid ${palette.grey[200]}`,
                    bgcolor: isSelected ? palette.primary.light : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: isSelected ? palette.primary.light : palette.grey[50],
                    },
                  }}
                >
                  {/* Icon badge */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: config.badgeBg,
                      color: config.badgeColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {config.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? palette.primary.main : 'text.primary',
                      textAlign: 'center',
                      lineHeight: 1.2,
                    }}
                  >
                    {config.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Type-specific options */}
        {renderOptionsConfig()}

        {optionsError && (
          <FormHelperText error sx={{ mt: 1 }}>
            {optionsError}
          </FormHelperText>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button
          onClick={closeModal}
          sx={{
            color: palette.text.secondary,
            '&:hover': { bgcolor: palette.grey[100] },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={validateAndSubmit}
          disabled={props.isLoading}
          variant="contained"
          sx={{ borderRadius: 50, px: 3 }}
        >
          {isEditMode ? 'Update Field' : 'Create Field'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
